import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PushGcpCorrectionJobParameters } from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useErrorHandling } from '../../../hooks/use-error-handling';

export const usePushGcpCorrectionJobMutation = () => {
  const { clearErrors: clearApiErrors, handleError, buildErrorList } = useErrorHandling();
  const queryClient = useQueryClient();
  const errorList = useMemo(() => buildErrorList(), [buildErrorList]);
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: async (params: PushGcpCorrectionJobParameters) => {
      return SkyMapAxiosServiceFactory.instance
        .createGeodataServiceV0()
        .pushGcpCorrectionJob(params);
    },
    retry: false,
    networkMode: 'always',
    onSuccess: (_, variables) => {
      // Invalidate the query to refetch the updated job status
      void queryClient.invalidateQueries({
        queryKey: ['fetchGcpCorrectionJob', variables.path.geodataId],
      });

      publish(SubscriptionTopic.ToastrMessage, {
        type: 'success',
        message: t('settings.gcpCorrectionJobSent', { ns: 'cloudProcessing' }),
      });
    },
    onMutate: () => clearApiErrors(),
    onError: (err) => handleError(err),
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    errorList,
  };
};
